import { useState } from 'react';
import { Button, Card, FormBanner, Input } from '@nextleap-al/admin-ui';

import { rememberSession } from '../lib/boot';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const body = new FormData();
      body.set('username', username);
      body.set('password', password);
      const res = await fetch('/login', { method: 'POST', body });
      if (res.ok) {
        // So the load below draws the page frame rather than opening on a blank one.
        rememberSession(true);
        // A full load rather than a state change, since the session cookie decides what the
        // app may ask for. `replace`, so Back does not land on a form that just redirects.
        window.location.replace('/');
        return;
      }
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setError(data.error || 'Sign-in failed.');
    } catch {
      setError('Cannot reach the server.');
    }
    setBusy(false);
    setPassword('');
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg-secondary)] p-6">
      <Card className="w-full max-w-sm">
        <form onSubmit={submit} className="space-y-4">
          <div className="text-center">
            <span
              className="mx-auto flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] bg-[var(--primary-light)] text-[var(--primary)]"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
              >
                <path d="M3 21h18" />
                <path d="M6.5 21V11" />
                <path d="M12 21V4" />
                <path d="M17.5 21v-6" />
              </svg>
            </span>
            <h1 className="mt-3 text-lg font-semibold text-[var(--text-primary)]">
              NextI Analytics
            </h1>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Usage, quality and improvement tracking for the documentation assistant.
            </p>
          </div>

          {error && <FormBanner kind="error">{error}</FormBanner>}

          <Input
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          <Button type="submit" className="w-full" isLoading={busy}>
            Sign in
          </Button>

          {/* Accounts are made by a superadmin, or from the command line, on purpose.
              Saying so here stops the search for a "register" link that will never exist. */}
          <p className="text-center text-xs text-[var(--text-muted)]">
            Accounts are created by an administrator.
          </p>
        </form>
      </Card>
    </div>
  );
}
